<template>
  <dialog
    ref="dialogEl"
    class="scrim"
    aria-labelledby="pvp-banlist-title"
    @cancel.prevent="emit('close')"
    @click="onScrimClick"
  >
    <div class="panel">
      <div class="head">
        <div>
          <p class="kicker">{{ t('deck.banlistKicker') }}</p>
          <h2 id="pvp-banlist-title">{{ t('deck.banlistTitle') }}</h2>
          <p class="lead">{{ t('deck.banlistLead') }}</p>
        </div>
        <CloseXButton :label="t('deck.banlistClose')" @click="emit('close')" />
      </div>
      <ul class="body">
        <li v-for="row in rows" :key="row.id" class="row">
          <span class="name">{{ row.name }}</span>
          <span class="counts">
            {{ t('deck.banlistRow', { have: row.count, max: row.max }) }}
          </span>
          <span class="limit-pill" aria-hidden="true">{{ row.max }}</span>
        </li>
      </ul>
      <div class="actions">
        <button type="button" class="ghost" @click="emit('close')">
          {{ t('deck.banlistClose') }}
        </button>
        <router-link to="/banlist" class="ghost link" @click="emit('close')">
          {{ t('nav.banlist') }}
        </router-link>
        <button type="button" class="cta" @click="emit('edit')">
          {{ t('deck.banlistEdit') }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { BanlistViolation } from '@shared/index'
import { localizedName } from '../i18n/localizeCard'
import { useI18n } from '../i18n/t'
import CloseXButton from './CloseXButton.vue'

const props = defineProps<{
  violations: BanlistViolation[]
}>()

const emit = defineEmits<{ close: []; edit: [] }>()
const { t } = useI18n()
const dialogEl = ref<HTMLDialogElement | null>(null)

const rows = computed(() =>
  props.violations.map((v) => ({
    ...v,
    name: localizedName(v.id),
  })),
)

onMounted(() => {
  dialogEl.value?.showModal()
})

function onScrimClick(event: MouseEvent): void {
  if (event.target === dialogEl.value) emit('close')
}
</script>

<style scoped>
.scrim {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  padding: max(16px, env(safe-area-inset-top))
    max(16px, env(safe-area-inset-right))
    max(16px, env(safe-area-inset-bottom))
    max(16px, env(safe-area-inset-left));
  border: 0;
  background: transparent;
  color: var(--md-sys-color-on-surface);
  overflow: hidden;
  animation: banlist-in var(--md-motion-medium, 280ms) var(--md-motion-standard, ease);
}

.scrim::backdrop {
  background: rgba(0, 0, 0, 0.55);
}

.panel {
  width: min(440px, 100%);
  max-height: min(80vh, 560px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 20px 16px;
  border-radius: 16px;
  background: var(--md-sys-color-surface-container-high, #2a2a2e);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
  overflow: hidden;
}

.head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.kicker {
  margin: 0 0 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f2b8b5;
}

h2 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
}

.lead {
  margin: 8px 0 0;
  font-size: 0.9rem;
  line-height: 1.4;
  opacity: 0.85;
}

.body {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
}

.row {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--md-sys-color-surface-container, #1e1e22);
}

.name {
  font-weight: 600;
  font-size: 0.92rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.counts {
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
  white-space: nowrap;
}

.limit-pill {
  min-width: 22px;
  padding: 2px 7px;
  border-radius: 999px;
  background: #b3261e;
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.ghost,
.cta {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 999px;
  border: 0;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ghost {
  background: transparent;
  color: inherit;
}

.cta {
  background: var(--md-sys-color-primary, #d0bcff);
  color: var(--md-sys-color-on-primary, #381e72);
}

@keyframes banlist-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .scrim {
    animation: none;
  }
}
</style>
